// api/client.js — Gateway 제품 경계(`/product/*`)의 유일한 접속면.
//
// 브라우저는 Gateway 주소도 토큰도 모른다. same-origin `/api/*` 로만 말하고,
// 세션은 httpOnly 쿠키 하나(`__Host-loom_session`)다 — JS 가 읽을 방법이 없다.
//
// v2 클라이언트에서 **없어서 문제였던 것 셋**을 여기서 채운다:
//   1. `POST /session/refresh` 호출이 아예 없었다 → idle 30분 뒤 조용히 세션이 끊겼다.
//   2. `Idempotency-Key` 를 매 호출 새로 만들어, 재시도가 곧 **중복 실행**이었다.
//   3. 401 을 만나면 그대로 위로 던져, 갱신 가능한 세션도 로그아웃처럼 보였다.

import { ProductError } from './errors.js';
import { isKnownProductPath } from './upload.js';

const API_BASE = '/api';
const TIMEOUT_MS = 12_000;

/** 세션 idle TTL 은 30분. 그 한참 전에 미리 회전시킨다. */
const REFRESH_EVERY_MS = 10 * 60 * 1000;

// ── 저수준 요청 ────────────────────────────────────────────────────────────

async function raw(path, { method = 'GET', headers = {}, body, signal } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const onOuterAbort = () => controller.abort();
  signal?.addEventListener('abort', onOuterAbort, { once: true });

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      // 쿠키를 실어 보내되, 다른 출처로는 절대 나가지 않는다.
      credentials: 'same-origin',
      headers: body === undefined ? headers : { 'content-type': 'application/json', ...headers },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (cause) {
    // fetch 자체가 실패한 것과, 서버가 오류를 **말해 준 것**은 다른 사건이다.
    throw new ProductError({
      code: cause?.name === 'AbortError' ? 'gateway_timeout' : 'gateway_unreachable',
      status: null,
      message: cause?.message ?? 'network failure',
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onOuterAbort);
  }

  const payload = await readJson(response);

  if (!response.ok) {
    const envelope = payload?.error ?? {};
    throw new ProductError({
      code: envelope.code ?? null,
      status: response.status,
      message: envelope.message ?? response.statusText,
      details: envelope.details ?? null,
    });
  }

  return payload;
}

async function readJson(response) {
  if (response.status === 204) return null;
  const text = await response.text();
  if (text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    // 본문이 JSON 이 아니면 Gateway 를 지나오지 않은 응답이다(프록시 오설정 등).
    throw new ProductError({
      code: 'gateway_unreachable',
      status: response.status,
      message: '서버가 JSON이 아닌 응답을 보냈습니다.',
    });
  }
}

// ── 세션 ──────────────────────────────────────────────────────────────────

let csrfToken = null;
let refreshTimer = null;

/** whoami 는 anchorRef·신원을 절대 주지 않는다. csrfToken 과 만료 시각뿐이다. */
export async function whoami() {
  const body = await raw('/session/whoami');
  csrfToken = body?.csrfToken ?? null;
  return body;
}

export async function refreshSession() {
  const body = await raw('/session/refresh', { method: 'POST', headers: csrfHeaders() });
  // 회전하면 csrfToken 도 새로 받는다.
  if (body?.csrfToken) csrfToken = body.csrfToken;
  return body;
}

export async function logout() {
  try {
    await raw('/session/logout', { method: 'POST', headers: csrfHeaders() });
  } finally {
    csrfToken = null;
    stopSessionKeepalive();
  }
}

/**
 * 로그인은 **전체 페이지 이동**이다(IdP 302). returnPath 는 경로만 보낸다 —
 * origin 은 서버가 붙인다(`safeReturnPath` 계약).
 */
export function login(returnPath = window.location.pathname + window.location.search) {
  window.location.assign(`${API_BASE}/session/login?returnPath=${encodeURIComponent(returnPath)}`);
}

/**
 * idle 만료 전에 미리 회전시킨다.
 * **absolute TTL(12시간)과 max auth age(1시간)는 이걸로 늘어나지 않는다** — 그때는
 * `reauth_required` 가 오고, 그건 재로그인만이 답이다.
 */
export function startSessionKeepalive(onLost) {
  stopSessionKeepalive();
  refreshTimer = setInterval(async () => {
    try {
      await refreshSession();
    } catch (error) {
      stopSessionKeepalive();
      onLost?.(error);
    }
  }, REFRESH_EVERY_MS);
}

export function stopSessionKeepalive() {
  if (refreshTimer !== null) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

function csrfHeaders() {
  if (!csrfToken) {
    throw new ProductError({ code: 'csrf_unavailable', status: null, message: 'csrf token not loaded' });
  }
  return { 'x-loom-csrf': csrfToken };
}

// ── 읽기 ──────────────────────────────────────────────────────────────────

/**
 * `GET /product/<name>`
 *
 * `entry` 하나만 nodeId 헤더 없이 호출된다(첫 진입 bootstrap). 나머지 12개는 필수다.
 * nodeId 는 "지금 어느 Node 로 말하는가"를 매 요청 지정하는 배선 값일 뿐,
 * 화면에 보이거나 저장될 값이 아니다.
 */
export async function read(name, { nodeId, query, signal } = {}) {
  const search = buildQuery(query);
  const headers = nodeId ? { 'x-loom-node-id': nodeId } : {};
  return withSessionRecovery(() => raw(`/product/${name}${search}`, { headers, signal }));
}

function buildQuery(query) {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s === '' ? '' : `?${s}`;
}

// ── 쓰기 ──────────────────────────────────────────────────────────────────

/**
 * `POST /product/commands/<operation>`
 *
 * **`idempotencyKey` 는 호출자가 준다.** 이유: 재시도 때 새 키를 만들면 서버 입장에서는
 * "새 요청"이 되어 멱등성이 깨진다. 키는 **시도 단위가 아니라 논리적 행위 단위**로 고정되어야
 * 한다. 안 주면 여기서 하나 만들지만, 그건 "재시도하지 않는 호출"이라는 선언이다.
 *
 * 그리고 이 함수는 **성공을 낙관하지 않는다.** 서버 응답 전에는 아무것도 돌려주지 않는다
 * (ux §5 공통 규칙 1: optimistic success 금지).
 */
export async function command(operation, params = {}, { nodeId, idempotencyKey, signal } = {}) {
  const headers = {
    ...csrfHeaders(),
    'idempotency-key': idempotencyKey ?? newActionKey(),
  };
  if (nodeId) headers['x-loom-node-id'] = nodeId;

  return withSessionRecovery(() =>
    raw(`/product/commands/${operation}`, { method: 'POST', headers, body: { params }, signal }),
  );
}

/**
 * Resource Cloud is a metadata registry, not an upload endpoint. The browser
 * only sends a locator manifest; bytes, bodies, tokens and provider secrets
 * never pass through this product client.
 */
export async function registerResource(params = {}, { nodeId, idempotencyKey, signal } = {}) {
  const headers = {
    ...csrfHeaders(),
    'idempotency-key': idempotencyKey ?? newActionKey(),
  };
  if (nodeId) headers['x-loom-node-id'] = nodeId;
  return withSessionRecovery(() =>
    raw('/product/resources', { method: 'POST', headers, body: params, signal }),
  );
}

// ── 파일 업로드 (docs/shared-file-api-v0.1.md) ─────────────────────────────
//
// 세 걸음으로 나뉜 이유가 이 파일에서 제일 잘 보인다: **JSON 본문에 파일을 싣지 않는다.**
// base64 는 바이트를 4/3 으로 불리고, 그 문자열을 만들려면 파일 전체가 한 번 더 메모리에
// 올라간다. 그래서 ② 는 `File` 을 **그대로** 몸통에 싣는 PUT 이다.
//
// 그리고 ② 만 `XMLHttpRequest` 다. `fetch` 는 업로드 진행률을 주지 않는다 —
// 25MiB 를 보내는 동안 화면이 아무 말도 못 하면, 멈춘 것과 구별할 수 없다.

/**
 * ① 예약. 여기서 서버가 할당량을 선점하고 `uploadId` 를 준다.
 * 같은 `Idempotency-Key` 로 다시 부르면 **같은 예약**이 돌아온다(새로 만들지 않는다).
 */
export async function initResourceUpload(params = {}, { nodeId, idempotencyKey, signal } = {}) {
  const headers = {
    ...csrfHeaders(),
    'idempotency-key': idempotencyKey ?? newActionKey(),
    'x-loom-node-id': nodeId,
  };
  return withSessionRecovery(() =>
    raw('/product/resource-uploads', { method: 'POST', headers, body: params, signal }),
  );
}

/**
 * ② 전송. 바이트 그대로, 진행률과 취소가 있는 유일한 호출.
 *
 * **`content-length` 를 우리가 적지 않는다.** 브라우저가 금지된 헤더로 막고, `File` 을
 * 몸통에 실으면 어차피 정확한 값을 자기가 붙인다. 손으로 적으려 하면 조용히 무시되거나
 * 던진다 — 어느 쪽이든 우리가 아니라 브라우저가 정할 일이다.
 *
 * `path` 는 예약 응답이 알려 준 값이지만 **모양을 확인한 뒤에만** 쓴다(`uploadStepPath`).
 * 서버가 준 문자열을 그대로 요청 주소로 삼으면, 그 값이 다른 origin 이 되는 날 세션
 * 쿠키와 CSRF 토큰이 그리로 따라 나간다.
 */
export function transferResourceUpload({ path, spaceId, nodeId, file, onProgress, signal }) {
  if (!isKnownProductPath(path)) {
    return Promise.reject(new ProductError({
      code: 'invalid_upload_path',
      status: null,
      message: '업로드 경로가 이 앱이 아는 제품 경로가 아닙니다.',
    }));
  }
  const token = csrfHeaders();
  const url = `${API_BASE}${path}?spaceId=${encodeURIComponent(spaceId)}`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.responseType = 'text';
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.setRequestHeader('x-loom-node-id', nodeId);
    xhr.setRequestHeader('x-loom-csrf', token['x-loom-csrf']);

    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort, { once: true });
    const done = () => signal?.removeEventListener('abort', onAbort);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.({ loaded: event.loaded, total: event.total });
    };
    xhr.onerror = () => {
      done();
      reject(new ProductError({ code: 'gateway_unreachable', status: null, message: '전송이 끊겼습니다.' }));
    };
    xhr.onabort = () => {
      done();
      reject(new ProductError({ code: 'upload_cancelled', status: null, message: '전송을 취소했습니다.' }));
    };
    xhr.ontimeout = () => {
      done();
      reject(new ProductError({ code: 'gateway_timeout', status: null, message: '전송이 시간 안에 끝나지 않았습니다.' }));
    };
    xhr.onload = () => {
      done();
      let payload = null;
      try {
        payload = xhr.responseText === '' ? null : JSON.parse(xhr.responseText);
      } catch {
        reject(new ProductError({
          code: 'gateway_unreachable',
          status: xhr.status,
          message: '서버가 JSON이 아닌 응답을 보냈습니다.',
        }));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const envelope = payload?.error ?? {};
        reject(new ProductError({
          code: envelope.code ?? null,
          status: xhr.status,
          message: envelope.message ?? xhr.statusText,
          details: envelope.details ?? null,
        }));
        return;
      }
      resolve(payload);
    };
    // `File` 을 그대로 싣는다. 읽지도, 자르지도, 인코딩하지도 않는다.
    xhr.send(file);
  });
}

/** ③ 확정. 같은 `uploadId` 로 다시 불러도 **판은 하나**다(첫 결과를 그대로 준다). */
export async function finalizeResourceUpload({ path, spaceId, nodeId, idempotencyKey, signal }) {
  if (!isKnownProductPath(path)) {
    throw new ProductError({ code: 'invalid_upload_path', status: null, message: '확정 경로가 올바르지 않습니다.' });
  }
  const headers = {
    ...csrfHeaders(),
    'idempotency-key': idempotencyKey ?? newActionKey(),
    'x-loom-node-id': nodeId,
  };
  return withSessionRecovery(() =>
    raw(`${path}?spaceId=${encodeURIComponent(spaceId)}`, { method: 'POST', headers, body: {}, signal }),
  );
}

/** ⑤ 취소. 예약을 닫고 임시 바이트를 지운다. 확정된 판은 이걸로 사라지지 않는다. */
export async function abortResourceUpload({ path, spaceId, nodeId, idempotencyKey, signal }) {
  if (!isKnownProductPath(path)) {
    throw new ProductError({ code: 'invalid_upload_path', status: null, message: '취소 경로가 올바르지 않습니다.' });
  }
  const headers = {
    ...csrfHeaders(),
    'idempotency-key': idempotencyKey ?? newActionKey(),
    'x-loom-node-id': nodeId,
  };
  return raw(`${path}?spaceId=${encodeURIComponent(spaceId)}`, { method: 'POST', headers, body: {}, signal });
}

/**
 * ④ 다운로드·미리보기. **매 요청 인증된다** — 영구 공개 주소도 만료 URL 도 없다.
 *
 * `disposition: 'inline'` 은 서버가 png·jpeg 에만 허용한다(다른 형식은 400 으로 거절된다).
 * 그래서 화면은 그 둘에만 미리보기를 건다 — PDF·HTML·SVG 를 앱 origin 에서 여는 길은 없다.
 *
 * 돌려주는 것은 `Blob` 이다. 화면이 `URL.createObjectURL` 로 쓰고 **반드시 되돌려 준다**.
 */
export async function fetchResourceContent({ spaceId, assetId, version, nodeId, disposition, signal }) {
  const params = new URLSearchParams({ spaceId, assetId });
  if (version) params.set('version', String(version));
  if (disposition) params.set('disposition', disposition);

  let response;
  try {
    response = await fetch(`${API_BASE}/product/resource-content?${params.toString()}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'x-loom-node-id': nodeId },
      signal,
    });
  } catch (cause) {
    throw new ProductError({
      code: cause?.name === 'AbortError' ? 'gateway_timeout' : 'gateway_unreachable',
      status: null,
      message: cause?.message ?? 'network failure',
    });
  }

  if (!response.ok) {
    let envelope = {};
    try {
      envelope = (await response.json())?.error ?? {};
    } catch {
      envelope = {};
    }
    throw new ProductError({
      code: envelope.code ?? null,
      status: response.status,
      message: envelope.message ?? response.statusText,
    });
  }

  return {
    blob: await response.blob(),
    mediaType: response.headers.get('content-type'),
    // 서버가 정한 파일 이름만 쓴다. 우리가 지어내지 않는다.
    filename: filenameFromDisposition(response.headers.get('content-disposition')),
    checksumSha256: response.headers.get('x-loom-asset-checksum-sha256'),
  };
}

function filenameFromDisposition(value) {
  if (typeof value !== 'string') return null;
  const extended = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (extended) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // 이어서 평문 filename 을 본다.
    }
  }
  const plain = value.match(/filename="([^"]*)"/i);
  return plain ? plain[1] : null;
}

/**
 * 브라우저가 계산하는 SHA-256.
 *
 * **메모리 한 덩어리를 쓴다.** WebCrypto 에는 스트리밍 digest 가 없어서
 * (`crypto.subtle.digest` 는 버퍼 하나를 받는다) 파일 전체가 한 번 `ArrayBuffer` 로 올라간다.
 * 그래서 **능력 표의 상한(`maxFileBytes`, 현재 25MiB)을 넘는 파일은 여기서 거절한다** —
 * 어차피 서버도 거절할 크기이고, 그 전에 브라우저 탭을 죽이지 않기 위해서다.
 * 사본은 하나뿐이다: base64 로 바꾸지도, 잘라 이어 붙이지도 않는다.
 *
 * 상한이 크게 늘어나면 이 자리는 증분 해시(스트리밍)로 바꿔야 한다. 지금 그렇게 하지
 * 않는 이유는 해시를 직접 구현하지 않기 위해서다.
 */
export async function digestFileSha256(file, { maxBytes = 0 } = {}) {
  const size = Number(file?.size ?? 0);
  if (maxBytes > 0 && size > maxBytes) {
    throw new ProductError({
      code: 'asset_file_too_large',
      status: null,
      message: '이 배포가 받는 파일 상한을 넘습니다.',
    });
  }
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * 온보딩은 일반 명령이 아니다 — `x-loom-node-id` 가 **없는 유일한 쓰기**다.
 * 아직 Node 가 없어서 하는 행위이므로 당연하지만, 이 구분을 지우면 조용히 깨진다.
 */
export async function onboardHumanNode(displayName, { idempotencyKey, signal } = {}) {
  const headers = {
    ...csrfHeaders(),
    'idempotency-key': idempotencyKey ?? newActionKey(),
  };
  return withSessionRecovery(() =>
    raw('/product/onboarding/node', { method: 'POST', headers, body: { displayName }, signal }),
  );
}

/**
 * AI Scout는 일반 command가 아니다. Gateway가 Scout 관계·anchor·provenance를
 * 직접 만들고 서명하므로, 웹은 이름과 사람이 적은 역할 설명만 보낸다.
 */
export async function scoutAiNode({ displayName, purpose }, { actorNodeId, idempotencyKey, signal } = {}) {
  const headers = {
    ...csrfHeaders(),
    'idempotency-key': idempotencyKey ?? newActionKey(),
    'x-loom-node-id': actorNodeId,
  };
  return withSessionRecovery(() =>
    raw('/product/scout/ai-node', { method: 'POST', headers, body: { displayName, purpose }, signal }),
  );
}

/**
 * Bridge API key는 원문이 한 번만 응답에 존재한다. 응답 유실 뒤 자동 재시도를 하면
 * 두 번째 키를 만들어 첫 번째 키가 사용 중인지를 사용자가 알 수 없게 된다. 따라서
 * 일반 command와 달리 `withSessionRecovery`를 거치지 않는다.
 */
export async function issueBridgeKey({ nodeId, label, actorNodeId }, { signal } = {}) {
  return raw('/product/bridge/api-keys', {
    method: 'POST',
    // header 는 이 발급을 승인하는 현재 사람 Node다. `nodeId`는 그 사람 Node 자신일 수도
    // 있다. 그 경우에도 Bridge 채널은 서버가 정한 delegated_ai 출처로만 동작하며,
    // 사람 직접 웹 세션으로 가장할 수 없다.
    headers: { ...csrfHeaders(), 'x-loom-node-id': actorNodeId },
    body: { nodeId, label },
    signal,
  });
}

/**
 * 폐기는 접근을 줄이는 멱등 행위다. 응답이 유실되어도 같은 keyId로 한 번 더 확인하는 것은
 * 새 자격을 만들지 않는다. 그래서 세션 갱신 뒤 한 번의 재시도만 허용한다.
 */
export async function revokeBridgeKey({ actorNodeId, keyId }, { signal } = {}) {
  return withSessionRecovery(() =>
    raw(`/product/bridge/api-keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
      headers: { ...csrfHeaders(), 'x-loom-node-id': actorNodeId },
      signal,
    }),
  );
}

/** 논리적 행위 하나에 붙는 키. 재시도해도 이 값을 **바꾸지 않는다**. */
export function newActionKey() {
  return crypto.randomUUID();
}

// ── 세션 회복 ─────────────────────────────────────────────────────────────

/**
 * 401 을 만나면 **한 번만** 갱신하고 다시 시도한다.
 *
 * - `reauth_required` 는 갱신으로 넘을 수 없으므로 그대로 던진다.
 * - 502 `upstream_rejected_claim` 은 401 처럼 보이지만 배선 문제다 — Gateway 가 이미
 *   502 로 바꿔서 주므로 여기 오지 않는다. 그게 그 변환의 목적이다.
 */
async function withSessionRecovery(attempt) {
  try {
    return await attempt();
  } catch (error) {
    if (!(error instanceof ProductError)) throw error;
    if (error.status !== 401) throw error;
    if (error.code === 'reauth_required') throw error;

    try {
      await refreshSession();
    } catch {
      throw error; // 갱신도 실패했으면 원래 실패를 말한다. 두 번 놀라게 하지 않는다.
    }
    return attempt();
  }
}
